import React, { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { contrastText, BRAND_DEFAULT } from "@/lib/color";

/**
 * BeforeAfterSlider
 * Premium clinical-grade before/after image comparator.
 */
export default function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeLabel = "Before",
  afterLabel = "After",
  aspect = "aspect-[4/5]",
  brandColor = BRAND_DEFAULT,
}) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);

  const updateFromEvent = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(2, Math.min(98, (x / rect.width) * 100));
    setPos(pct);
  }, []);

  const onMouseDown = (e) => {
    e.preventDefault();
    const onMove = (ev) => updateFromEvent(ev.clientX);
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onTouchMove = (e) => {
    if (e.touches[0]) updateFromEvent(e.touches[0].clientX);
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${aspect} overflow-hidden bg-bone-100 select-none`}
      data-testid="before-after-slider"
      onTouchMove={onTouchMove}
      style={{ touchAction: "pan-y" }}
    >
      <img
        src={afterSrc}
        alt="After clear aligner smile"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={beforeSrc}
          alt="Before clear aligner smile"
          className="absolute inset-0 h-full object-cover"
          style={{ width: `${100 * (100 / pos)}%`, maxWidth: "none" }}
          draggable={false}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 overline bg-white/85 px-2 py-1 backdrop-blur-sm">
        {beforeLabel}
      </div>
      <div
        className="absolute top-4 right-4 overline px-2 py-1"
        style={{ backgroundColor: brandColor, color: contrastText(brandColor) }}
      >
        {afterLabel}
      </div>

      {/* Divider Line */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
        style={{ left: `${pos}%` }}
      />

      {/* Handle */}
      <motion.button
        type="button"
        aria-label="Drag to compare"
        data-testid="slider-handle"
        onMouseDown={onMouseDown}
        whileHover={{ scale: 1.05 }}
        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-ew-resize border border-bone-200"
        style={{ left: `${pos}%` }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={brandColor} strokeWidth="1.5">
          <path d="M9 6l-4 6 4 6M15 6l4 6-4 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </motion.button>
    </div>
  );
}
